import type { DimensionScore, RubricDimension } from "admin/types/interfaces/diagnostics";
import { scoreBg, scoreColor } from "admin/utils/scoring";
import { cn } from "~/lib/utils";

interface ScoreBarProps {
  score: number | null;
  label: string;
  /** Tighter variant for the in-session rail: narrower label, thinner bar, smaller type. */
  compact?: boolean;
}

/** One rubric dimension as a labelled 0-5 bar. `null` scores read as N/A. */
export const ScoreBar = ({ score, label, compact = false }: ScoreBarProps) => {
  const isNA = score == null;
  return (
    <div className={cn("flex items-center", compact ? "gap-2" : "gap-3")}>
      <span
        className={cn(
          "text-muted-foreground truncate shrink-0",
          compact ? "w-[136px] font-mono text-[10px]" : "w-[180px] text-sm"
        )}
        title={label}
      >
        {label}
      </span>
      <div
        className={cn("flex-1 rounded-full bg-muted overflow-hidden", compact ? "h-1.5" : "h-2")}
      >
        {/* An unscored dimension leaves the track empty. A filled bar in any tint
            reads as a score at a glance, and a full-width one reads as a 5. */}
        {!isNA && (
          <div
            className={cn("h-full rounded-full transition-all", scoreBg(score))}
            style={{ width: `${(score / 5) * 100}%` }}
          />
        )}
      </div>
      <span
        className={cn(
          "font-medium tabular-nums text-right",
          compact ? "w-7 text-[10px]" : "w-8 text-sm",
          scoreColor(score)
        )}
      >
        {isNA ? "N/A" : score}
      </span>
    </div>
  );
};

/** Rubric order, with N/A (null-score) dimensions at the bottom. */
export function sortDimensionScores(
  scores: DimensionScore[],
  rubricDimensions: RubricDimension[]
): DimensionScore[] {
  const dimOrder = new Map(rubricDimensions.map((d) => [d.name, d.sortOrder]));
  return [...scores].sort((a, b) => {
    if (a.score == null && b.score != null) return 1;
    if (a.score != null && b.score == null) return -1;
    // N.B. Both ranks are Infinity whenever the dimension is absent from the
    // rubric - which is every dimension for the in-session rail, since rubric
    // display names come from the unimplemented `/summary` endpoint. Subtracting
    // them would be NaN, and a NaN comparator result is unspecified; compare
    // instead so unranked pairs are explicitly equal and keep their given order.
    const rankA = dimOrder.get(a.dimension) ?? Infinity;
    const rankB = dimOrder.get(b.dimension) ?? Infinity;
    if (rankA === rankB) return 0;
    return rankA < rankB ? -1 : 1;
  });
}
